import React from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useSelector } from "react-redux";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  Snackbar,
} from "@mui/material";
import SchoolIcon from "@mui/icons-material/School";
import { styled, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Link } from "react-router-dom";
import LazyImage from "../../components/LazyLoadingImage/LazyImage";
import { PageLayout } from "../../components/PageLayout/PageLayout";
import Seo from "../../components/Seo/Seo";
import WhatsAppInfoSection from "../../components/WhatsAppInfoSection/WhatsAppInfoSection";
import { PAGE_SEO } from "../../seo/seoConstants";
import { WhatsAppButton } from "../shared/styledComponents";
import { EDUCATION_PAGE_TEXTS } from "../shared/constants";
import { gaEvent } from "../../analytics";
import { resolveAssetUrl } from "../../utils/assets";

import useUpdateAuthToken from "../../hooks/useUpdateAuthToken";
import {
  useGetEducationItemsQuery,
  useGetEnrollmentsQuery,
  useCreateEnrollmentMutation,
} from "../../services/education/education";
import { EducationItem, Enrollment } from "../../types/types";
import type { RootState } from "../../app/store";

const HeroBox = styled(Box)(({ theme }) => ({
  background: theme.palette.text.primary,
  color: theme.palette.common.white,
  padding: theme.spacing(4),
  borderRadius: theme.spacing(1),
  marginBottom: theme.spacing(1),
  textAlign: "center",
}));

const CourseCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.spacing(1),
}));

const LoginBox = styled(Box)(({ theme }) => ({
  textAlign: "center",
  padding: theme.spacing(2),
  background: theme.palette.background.default,
  borderRadius: theme.spacing(1),
  marginBottom: theme.spacing(1),
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: "bold",
  marginBottom: theme.spacing(1),
  marginTop: theme.spacing(1),
}));

const LoginButton = styled("button")(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.common.white,
  border: "none",
  padding: `${theme.spacing(1.5)} ${theme.spacing(4)}`,
  borderRadius: theme.spacing(1),
  fontFamily: "inherit",
  fontSize: "1rem",
  fontWeight: "bold",
  cursor: "pointer",
  "&:hover": {
    opacity: 0.9,
  },
}));

const getCategoryLabel = (category: EducationItem["category"]) =>
  category === "program"
    ? EDUCATION_PAGE_TEXTS.categoryProgram
    : EDUCATION_PAGE_TEXTS.categoryCourse;

const getEnrollmentStatusLabel = (status: Enrollment["status"]) => {
  const labels: Record<Enrollment["status"], string> = {
    enrolled: EDUCATION_PAGE_TEXTS.statusEnrolled,
    completed: EDUCATION_PAGE_TEXTS.statusCompleted,
    dropped: EDUCATION_PAGE_TEXTS.statusDropped,
  };
  return labels[status];
};

const getEnrollmentStatusColor = (status: Enrollment["status"]): "success" | "info" | "default" => {
  const colors: Record<Enrollment["status"], "success" | "info" | "default"> = {
    enrolled: "info",
    completed: "success",
    dropped: "default",
  };
  return colors[status];
};

const CourseThumbnail = styled("div")({
  flexShrink: 0,
  "& img": {
    width: 260,
    height: "auto",
    maxHeight: 340,
    objectFit: "contain",
    borderRadius: 8,
  },
});

interface CourseCardItemProps {
  educationItem: EducationItem;
  isEnrolled: boolean;
  isAuthenticated: boolean;
  onEnroll: (educationItemId: number) => void;
  isEnrolling: boolean;
  isSmall: boolean;
}

const EnrollAction: React.FC<{
  educationItem: EducationItem;
  isEnrolled: boolean;
  isAuthenticated: boolean;
  onEnroll: (educationItemId: number) => void;
  isEnrolling: boolean;
}> = ({ educationItem, isEnrolled, isAuthenticated, onEnroll, isEnrolling }) => {
  if (!isAuthenticated) return null;
  return isEnrolled ? (
    <Chip label={EDUCATION_PAGE_TEXTS.alreadyEnrolled} color="success" />
  ) : (
    <WhatsAppButton
      onClick={() => onEnroll(educationItem.id)}
      disabled={isEnrolling}
      sx={{
        backgroundColor: "primary.main",
        "&:hover": { backgroundColor: "secondary.main" },
      }}
    >
      {isEnrolling ? EDUCATION_PAGE_TEXTS.enrolling : EDUCATION_PAGE_TEXTS.enrollButton}
    </WhatsAppButton>
  );
};

const CourseCardItem: React.FC<CourseCardItemProps> = ({
  educationItem,
  isEnrolled,
  isAuthenticated,
  onEnroll,
  isEnrolling,
  isSmall,
}) => {
  const thumbnail = resolveAssetUrl(educationItem.picture ?? educationItem.images?.[0]);

  if (isSmall) {
    return (
      <CourseCard>
        <CardContent>
          <Box display="flex" alignItems="center" gap={1} mb={1} flexWrap="wrap">
            <SchoolIcon color="primary" />
            <Typography variant="h6" fontWeight="bold">
              {educationItem.name}
            </Typography>
            <Chip label={getCategoryLabel(educationItem.category)} size="small" color="primary" />
          </Box>
          {thumbnail && (
            <Box mb={1}>
              <LazyImage
                src={thumbnail}
                alt={educationItem.name}
                style={{ width: "100%", objectFit: "contain", borderRadius: 4 }}
              />
            </Box>
          )}
          {educationItem.description && (
            <Typography variant="body2" mb={1}>
              {educationItem.description}
            </Typography>
          )}
          <Box display="flex" gap={2} flexWrap="wrap" mb={1}>
            {educationItem.duration && (
              <Typography variant="body2" color="text.secondary">
                {EDUCATION_PAGE_TEXTS.duration}: {educationItem.duration}
              </Typography>
            )}
            {educationItem.start_date && educationItem.end_date && (
              <Typography variant="body2" color="text.secondary">
                {EDUCATION_PAGE_TEXTS.dates}: {educationItem.start_date} — {educationItem.end_date}
              </Typography>
            )}
            {educationItem.max_capacity > 0 && (
              <Typography variant="body2" color="text.secondary">
                {EDUCATION_PAGE_TEXTS.capacity}: {educationItem.max_capacity}
              </Typography>
            )}
          </Box>
          <EnrollAction
            educationItem={educationItem}
            isEnrolled={isEnrolled}
            isAuthenticated={isAuthenticated}
            onEnroll={onEnroll}
            isEnrolling={isEnrolling}
          />
        </CardContent>
      </CourseCard>
    );
  }

  return (
    <CourseCard>
      <CardContent>
        <Box display="flex" alignItems="stretch" gap={3}>
          {thumbnail && (
            <CourseThumbnail>
              <LazyImage src={thumbnail} alt={educationItem.name} />
            </CourseThumbnail>
          )}
          <Box flex={1} display="flex" flexDirection="column" justifyContent="space-between">
            <Box>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <SchoolIcon color="primary" />
                <Typography variant="h5" fontWeight="bold">
                  {educationItem.name}
                </Typography>
                <Chip
                  label={getCategoryLabel(educationItem.category)}
                  size="small"
                  color="primary"
                />
              </Box>
              {educationItem.description && (
                <Typography variant="body1" mb={2}>
                  {educationItem.description}
                </Typography>
              )}
              <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
                {educationItem.duration && (
                  <Typography variant="body2" color="text.secondary">
                    {EDUCATION_PAGE_TEXTS.duration}: {educationItem.duration}
                  </Typography>
                )}
                {educationItem.start_date && educationItem.end_date && (
                  <Typography variant="body2" color="text.secondary">
                    {EDUCATION_PAGE_TEXTS.dates}: {educationItem.start_date} —{" "}
                    {educationItem.end_date}
                  </Typography>
                )}
                {educationItem.max_capacity > 0 && (
                  <Typography variant="body2" color="text.secondary">
                    {EDUCATION_PAGE_TEXTS.capacity}: {educationItem.max_capacity}
                  </Typography>
                )}
              </Box>
            </Box>
            <Box>
              <EnrollAction
                educationItem={educationItem}
                isEnrolled={isEnrolled}
                isAuthenticated={isAuthenticated}
                onEnroll={onEnroll}
                isEnrolling={isEnrolling}
              />
            </Box>
          </Box>
        </Box>
      </CardContent>
    </CourseCard>
  );
};

const EducationPage: React.FC = () => {
  const { isAuthenticated, loginWithRedirect } = useAuth0();
  useUpdateAuthToken();
  const token = useSelector((state: RootState) => state.auth.token);
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));

  const {
    data: educationItems,
    isLoading: itemsLoading,
    error: itemsError,
  } = useGetEducationItemsQuery();
  const { data: enrollments } = useGetEnrollmentsQuery(undefined, {
    skip: !isAuthenticated || !token,
  });
  const [createEnrollment, { isLoading: isEnrolling }] = useCreateEnrollmentMutation();

  const [snackbar, setSnackbar] = React.useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  const enrolledItemIds = React.useMemo(
    () => new Set(enrollments?.map((e) => e.education_item_id) ?? []),
    [enrollments],
  );

  const handleEnroll = async (educationItemId: number) => {
    if (!isAuthenticated || !token) return;
    try {
      const educationItem = educationItems?.find((i) => i.id === educationItemId);
      gaEvent("education_enroll", {
        course_name: educationItem?.name ?? `#${educationItemId}`,
        method: "whatsapp",
      });
      await createEnrollment({ education_item: educationItemId }).unwrap();
      setSnackbar({ open: true, message: EDUCATION_PAGE_TEXTS.enrollSuccess, severity: "success" });
    } catch {
      setSnackbar({ open: true, message: EDUCATION_PAGE_TEXTS.enrollError, severity: "error" });
    }
  };

  const handleLogin = async () => {
    gaEvent("login_start");
    await loginWithRedirect({
      appState: { returnTo: "/educacion" },
      authorizationParams: { prompt: "login" },
    });
  };

  const activeEducationItems =
    educationItems?.filter((educationItem) => educationItem.status === "active") ?? [];

  return (
    <PageLayout>
      <Seo {...PAGE_SEO["/educacion"]} />
      <Box p={2}>
        <HeroBox>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            {EDUCATION_PAGE_TEXTS.title}
          </Typography>
          <Typography variant="h6" gutterBottom>
            {EDUCATION_PAGE_TEXTS.subtitle}
          </Typography>
          <Typography variant="body1">{EDUCATION_PAGE_TEXTS.description}</Typography>
        </HeroBox>

        {!isAuthenticated && (
          <LoginBox>
            <Typography variant="body1" mb={2}>
              {EDUCATION_PAGE_TEXTS.loginPrompt}
            </Typography>
            <LoginButton onClick={handleLogin}>{EDUCATION_PAGE_TEXTS.loginButton}</LoginButton>
          </LoginBox>
        )}

        <SectionTitle variant="h5">{EDUCATION_PAGE_TEXTS.coursesTitle}</SectionTitle>

        {itemsLoading && (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
            <Typography ml={2}>{EDUCATION_PAGE_TEXTS.coursesLoading}</Typography>
          </Box>
        )}

        {itemsError && <Alert severity="error">{EDUCATION_PAGE_TEXTS.coursesError}</Alert>}

        {!itemsLoading && !itemsError && activeEducationItems.length === 0 && (
          <Typography color="text.secondary">{EDUCATION_PAGE_TEXTS.noCourses}</Typography>
        )}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr",
            gap: 2,
          }}
        >
          {activeEducationItems.map((educationItem) => (
            <Link
              key={educationItem.id}
              to={`/educacion/${educationItem.slug}`}
              style={{ textDecoration: "none", color: "inherit" }}
            >
              <CourseCardItem
                educationItem={educationItem}
                isEnrolled={enrolledItemIds.has(String(educationItem.id))}
                isAuthenticated={isAuthenticated}
                onEnroll={handleEnroll}
                isEnrolling={isEnrolling}
                isSmall={isSmall}
              />
            </Link>
          ))}
        </Box>

        {isAuthenticated && enrollments && (
          <>
            <SectionTitle variant="h5">{EDUCATION_PAGE_TEXTS.myEnrollmentsTitle}</SectionTitle>
            {enrollments.length === 0 ? (
              <Typography color="text.secondary">{EDUCATION_PAGE_TEXTS.noEnrollments}</Typography>
            ) : (
              enrollments.map((enrollment) => {
                const educationItem = educationItems?.find(
                  (i) => String(i.id) === enrollment.education_item_id,
                );
                return (
                  <Card key={enrollment.education_item_id} sx={{ mb: 2, borderRadius: 1 }}>
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography fontWeight="bold">
                          {educationItem?.name ?? `Programa #${enrollment.education_item_id}`}
                        </Typography>
                        <Chip
                          label={getEnrollmentStatusLabel(enrollment.status)}
                          color={getEnrollmentStatusColor(enrollment.status)}
                          size="small"
                        />
                      </Box>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </>
        )}

        <WhatsAppInfoSection
          title={EDUCATION_PAGE_TEXTS.moreInfoTitle}
          description={EDUCATION_PAGE_TEXTS.moreInfoDescription}
          message={EDUCATION_PAGE_TEXTS.moreInfoWhatsappMessage}
          button={EDUCATION_PAGE_TEXTS.moreInfoWhatsappButton}
        />
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageLayout>
  );
};

export default EducationPage;
